import React, { useState } from 'react'
import { HelmetFunc } from 'components/PageMetaData'
import { useTranslation } from 'react-i18next'
import { Layout } from 'layout/Layout'
import { DefaultFormBlock } from 'blocks/DefaultFormBlock'
import styled from 'styled-components'
import { backgroundColors, colors } from 'styles/colors'
import { mobileAfterBorder } from 'styles/mobileAfterBorder'
import { displayWidth } from 'styles/width'
import { Container } from 'components/Container'
import { FAQItem } from 'blocks/FAQ/FAQItem'
import { graphql } from 'gatsby'
import { getDataByLanguage } from 'utils/getDataByLanguage'
import { ArchProjectSliderBlock } from 'blocks/ArchProjectSliderBlock'
import { LeftSidebar, RightSidebar } from 'components/SideBar'
import { TitleH1 } from 'components/TitleComponent'
import { indent } from 'styles/indent'
import { headerBg } from 'styles/headerBg'
import { searchStringToObject } from 'use-query-params'

const pageMetadata = {
    uk: {
        title:
            'Зразки архітектурних проектів: Ескізний, Робочий, Розширений | Clearline',
        description:
            'Готові приклади архітектурних проектів від студії ClearLine: ескізний проект, робочий проект і розширений робочий проект. Проекти містять набір матеріалів, креслень і документації для будівництва та дизайну.',
    },
    ru: {
        title:
            'Образцы архитектурных проектов: Эскизный, Рабочий, Расширенный | Clearline',
        description:
            'Готовые примеры архитектурных проектов от студии ClearLine: эскизный проект, рабочий проект и расширенный рабочий проект. Проекты содержат набор материалов, чертежей и документации для строительства и дизайна.',
    },
    en: {
        title:
            'Architectural project samples: Sketch, Working, Extended | Clearline',
        description:
            'Ready examples of architectural projects from ClearLine studio: sketch project, working project and extended working project. Projects contain a set of materials, drawings and documentation for construction and design.',
    },
}

const PageH1Wrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${backgroundColors.contact};
    position: relative;
    border-bottom: 1px solid ${colors.dark};
    ${mobileAfterBorder}
    :before {
        ${headerBg}
    }
`

const PageH1 = styled(TitleH1)`
    width: 100%;
    text-align: center;
    margin: 40px ${indent.mobile};
    @media (min-width: ${displayWidth.tablet}) {
        margin: 56px ${indent.heroColumnTablet};
        text-align: left;
    }
    @media (min-width: ${displayWidth.desktop}) {
        margin: 60px ${indent.heroColumnDesktop};
    }
`

const FaqWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${backgroundColors.project};
    position: relative;
    border-bottom: 1px solid ${colors.dark};
    ${mobileAfterBorder}
`

const FaqTitle = styled.h2`
    font-family: 'Yeseva One', sans-serif;
    font-size: 28px;
    line-height: 34px;
    letter-spacing: 1px;
    color: ${colors.dark};
    text-align: center;
    margin: 40px ${indent.mobile} 0;
    @media (min-width: ${displayWidth.tablet}) {
        text-align: left;
        margin: 40px ${indent.heroColumnTablet} 0;
    }
    @media (min-width: ${displayWidth.desktop}) {
        margin: 56px ${indent.heroColumnDesktop} 0;
        font-size: 32px;
    }
`

const FaqListStyled = styled.div`
    display: flex;
    flex-direction: column;
    padding: 28px 33px 64px;
    box-sizing: border-box;
    border-bottom: 1px solid ${colors.dark};
    @media (min-width: ${displayWidth.tablet}) {
        padding: 40px 48px 64px;
        border-bottom: none;
    }
    @media (min-width: ${displayWidth.desktop}) {
        padding: 56px 48px;
    }
`

interface ImageNode {
    node: {
        id: string
        childImageSharp: {
            fluid: {
                src: string
                srcSet: string
                base64: string
                aspectRatio: number
                sizes: string
            }
            parent: { name: string }
        }
    }
}

const ExamplePage = ({
    data,
    location,
}: {
    data: {
        sketchImagesRu: { edges: ImageNode[] }
        sketchImagesUk: { edges: ImageNode[] }
        workingImagesRu: { edges: ImageNode[] }
        workingImagesUk: { edges: ImageNode[] }
        allExampleArchitectFaqYaml: {
            edges: {
                node: {
                    items: { question: string; answer: string }[]
                    parent: { name: string }
                }
            }[]
        }
        allExampleSketchImagesDataYaml: {
            edges: {
                node: {
                    title: string
                    downloadText: string
                    compositionTitle: string
                    items: { title: string; alt: string }[]
                    parent: { name: string }
                }
            }[]
        }
        allExampleWorkingImagesDataYaml: {
            edges: {
                node: {
                    title: string
                    downloadText: string
                    compositionTitle: string
                    items: { title: string; alt: string }[]
                    parent: { name: string }
                }
            }[]
        }
    }
    location: { search: string }
}) => {
    const { i18n } = useTranslation()
    const queryParams = searchStringToObject(location.search)
    const shouldOpenModal = queryParams.openModal?.includes('true')

    const faqData = getDataByLanguage(
        data.allExampleArchitectFaqYaml,
        i18n.language
    )

    const sketchData = getDataByLanguage(
        data.allExampleSketchImagesDataYaml,
        i18n.language
    )
    const workingData = getDataByLanguage(
        data.allExampleWorkingImagesDataYaml,
        i18n.language
    )

    const sketchCompositionItems: string[] = (sketchData.items ?? []).map(
        (item: { title: string }) => item.title
    )

    const workingCompositionItems: string[] = (workingData.items ?? []).map(
        (item: { title: string }) => item.title
    )

    const isUk = i18n.language !== 'ru'
    const sketchImages = isUk
        ? data.sketchImagesUk.edges
        : data.sketchImagesRu.edges
    const workingImages = isUk
        ? data.workingImagesUk.edges
        : data.workingImagesRu.edges
    const sketchPdfUrl = isUk
        ? 'https://clearline.com.ua/sketch-project-uk.pdf'
        : 'https://clearline.com.ua/sketch-project-ru.pdf'
    const workingPdfUrl = isUk
        ? 'https://clearline.com.ua/working-project-uk.pdf'
        : 'https://clearline.com.ua/working-project-ru.pdf'

    const h1Titles: Record<string, string> = {
        ru: 'Примеры архитектурных проектов: эскизный, рабочий, расширенный',
        uk: 'Приклади архітектурних проектів: ескізний, робочий, розширений',
        en: 'Examples of architectural projects: sketch, working, extended',
    }

    const formHeroTitles: Record<string, string> = {
        ru: 'Хотите получить консультацию архитектора?',
        uk: "Хочете отримати консультацію архітектора?",
        en: 'Would you like to get an architect consultation?',
    }

    const faqTitles: Record<string, string> = {
        ru:
            'Часто задаваемые вопросы о составе и разработке архитектурных проектов',
        uk: 'Часті запитання про склад та розробку архітектурних проектів',
        en:
            'Frequently asked questions about architectural project composition and design',
    }

    const [isAnswerVisible, setIsAnswerVisible] = useState(-1)
    const lang = i18n.language

    return (
        <Layout>
            <HelmetFunc data={pageMetadata} />
            <PageH1Wrapper>
                <LeftSidebar background={backgroundColors.contact} />
                <Container columns={'1fr'} tabletColumns={'1fr'}>
                    <PageH1>{h1Titles[lang] ?? h1Titles.ru}</PageH1>
                </Container>
                <RightSidebar />
            </PageH1Wrapper>
            <ArchProjectSliderBlock
                title={sketchData.title}
                images={sketchImages}
                imagesData={sketchData.items ?? []}
                compositionTitle={sketchData.compositionTitle}
                compositionItems={sketchCompositionItems}
                eventPrefix="Sketch"
                pdfUrl={sketchPdfUrl}
                downloadText={sketchData.downloadText}
            />
            <ArchProjectSliderBlock
                title={workingData.title}
                images={workingImages}
                imagesData={workingData.items ?? []}
                compositionTitle={workingData.compositionTitle}
                compositionItems={workingCompositionItems}
                eventPrefix="Working"
                pdfUrl={workingPdfUrl}
                downloadText={workingData.downloadText}
                autoOpenModal={shouldOpenModal}
            />
            <FaqWrapper>
                <LeftSidebar background={backgroundColors.project} />
                <Container columns={'1fr'} tabletColumns={'1fr'}>
                    <FaqTitle>{faqTitles[lang] ?? faqTitles.ru}</FaqTitle>
                    <FaqListStyled>
                        {(faqData.items ?? []).map(
                            (
                                item: {
                                    question: string
                                    answer: string
                                },
                                index: number
                            ) => (
                                <FAQItem
                                    key={index}
                                    question={item.question}
                                    answer={item.answer}
                                    isAnswerVisible={isAnswerVisible}
                                    setIsAnswerVisible={setIsAnswerVisible}
                                    name={index}
                                />
                            )
                        )}
                    </FaqListStyled>
                </Container>
                <RightSidebar />
            </FaqWrapper>
            <DefaultFormBlock
                withPhoneMobile
                heroText={formHeroTitles[lang] ?? formHeroTitles.ru}
                tracking={{
                    conversionType: 'FormExample',
                    eventCategory: 'FormExample',
                }}
            />
        </Layout>
    )
}

export default ExamplePage

export const query = graphql`
    query {
        sketchImagesRu: allFile(
            filter: {
                relativeDirectory: { eq: "eskizeProjectRu" }
                extension: { in: ["jpg", "jpeg", "png", "webp"] }
            }
            sort: { fields: name }
        ) {
            edges {
                node {
                    id
                    childImageSharp {
                        fluid(srcSetBreakpoints: [3000], quality: 100) {
                            ...GatsbyImageSharpFluid
                        }
                        parent {
                            ... on File {
                                name
                            }
                        }
                    }
                }
            }
        }
        sketchImagesUk: allFile(
            filter: {
                relativeDirectory: { eq: "eskizeProjectUk" }
                extension: { in: ["jpg", "jpeg", "png", "webp"] }
            }
            sort: { fields: name }
        ) {
            edges {
                node {
                    id
                    childImageSharp {
                        fluid(srcSetBreakpoints: [3000], quality: 100) {
                            ...GatsbyImageSharpFluid
                        }
                        parent {
                            ... on File {
                                name
                            }
                        }
                    }
                }
            }
        }
        workingImagesRu: allFile(
            filter: {
                relativeDirectory: { eq: "workProjectRu" }
                extension: { in: ["jpg", "jpeg", "png", "webp"] }
            }
            sort: { fields: name }
        ) {
            edges {
                node {
                    id
                    childImageSharp {
                        fluid(srcSetBreakpoints: [3000], quality: 100) {
                            ...GatsbyImageSharpFluid
                        }
                        parent {
                            ... on File {
                                name
                            }
                        }
                    }
                }
            }
        }
        workingImagesUk: allFile(
            filter: {
                relativeDirectory: { eq: "workProjectUk" }
                extension: { in: ["jpg", "jpeg", "png", "webp"] }
            }
            sort: { fields: name }
        ) {
            edges {
                node {
                    id
                    childImageSharp {
                        fluid(srcSetBreakpoints: [3000], quality: 100) {
                            ...GatsbyImageSharpFluid
                        }
                        parent {
                            ... on File {
                                name
                            }
                        }
                    }
                }
            }
        }
        allExampleArchitectFaqYaml {
            edges {
                node {
                    items {
                        question
                        answer
                    }
                    parent {
                        ... on File {
                            name
                        }
                    }
                }
            }
        }
        allExampleSketchImagesDataYaml {
            edges {
                node {
                    title
                    downloadText
                    compositionTitle
                    items {
                        title
                        alt
                    }
                    parent {
                        ... on File {
                            name
                        }
                    }
                }
            }
        }
        allExampleWorkingImagesDataYaml {
            edges {
                node {
                    title
                    downloadText
                    compositionTitle
                    items {
                        title
                        alt
                    }
                    parent {
                        ... on File {
                            name
                        }
                    }
                }
            }
        }
    }
`
